import { useState } from 'react';

/**
 * usePagination — no page/pageSize/totalPages pattern existed anywhere in the app before this;
 * every table just rendered its full result set. Slices `items` for the caller and clamps the
 * current page back down whenever a filter shrinks the result set out from under it (otherwise
 * a user on page 5 of a filtered view could land on an empty page after broadening a filter).
 *
 * The clamp corrects `page` directly during render rather than in an effect - React's own
 * documented pattern for "adjusting state when a prop/derived value changes" (a stray setState
 * call here is bailed out and re-rendered before anything paints, so it never flashes the
 * stale/out-of-range page), which also means it never fires an extra effect-driven render.
 */
export function usePagination<T>(items: T[], pageSize = 25) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  if (page > totalPages) {
    setPage(totalPages);
  }

  const clampedPage = Math.min(page, totalPages);
  const pageItems = items.slice((clampedPage - 1) * pageSize, clampedPage * pageSize);

  return { page: clampedPage, setPage, totalPages, pageItems, pageSize, totalItems: items.length };
}
